import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";

import Cookies from "js-cookie";

import { useToastStore } from "@/stores/toast.store";
import { VERSION } from "@/constants/API";
import { CONSTANTS_KEYS } from "@/constants/store";
import { getActiveStoreKey } from "@/utils/store-context";

interface ErrorResponse {
  message?: string;
  error?: string;
}

const apiClient: AxiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});
export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  version?: string;
  hideToast?: boolean;
}
apiClient.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    try {
      const token = Cookies.get(CONSTANTS_KEYS.AUTH_TOKEN);

      // Priority: URL query param → localStorage (race-condition-safe)
      const storeKey = getActiveStoreKey();
      if (storeKey) {
        config.headers["X-Store-Key"] = storeKey;
      }

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Error while fetching token:", error);
    }
    const version = config.version || VERSION;
    config.baseURL =
      (process.env.NEXT_PUBLIC_API_BASE_URL || "http://0.0.0.0:7000") +
      "/" +
      version;
    return config;
  },
  (error: AxiosError) => {
    console.error("Error:", error);
    return Promise.reject(error);
  },
);


apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig & {
      _retry?: boolean;
    };
    const status = error.response?.status || 500;

    if (status === 401 && !originalRequest?._retry) {
      const storedRefresh = Cookies.get(CONSTANTS_KEYS.REFRESH_TOKEN);
      if (storedRefresh) {
        originalRequest._retry = true;
        try {
          const res = await axios.post(
            `${process.env.NEXT_PUBLIC_API_BASE_URL}/v1/auth/refresh`,
            { refreshToken: storedRefresh },
          );
          const newToken: string | undefined = res.data?.data?.access_token;
          if (newToken) {
            Cookies.set(CONSTANTS_KEYS.AUTH_TOKEN, newToken, { expires: 365 });
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
          }
        } catch {
          // fall through to logout
        }
      }
      Cookies.remove(CONSTANTS_KEYS.AUTH_TOKEN);
      Cookies.remove(CONSTANTS_KEYS.REFRESH_TOKEN);
      window.location.href = "/login";
      return Promise.reject(error);
    }

    if (originalRequest?.hideToast) {
      return Promise.reject(error);
    }

    const errorData = error.response?.data as ErrorResponse;
    const message =
      errorData?.message ||
      errorData?.error ||
      error.message ||
      "Something went wrong";

    if (status === 403) {
      useToastStore
        .getState()
        .showToast(
          "error",
          "Forbidden",
          "You do not have permission to access this resource.",
        );
    } else {
      useToastStore.getState().showToast("error", `Error ${status}`, message);
    }

    if (status === 418) {
      window.location.href = "/login";
    } else if (status === 422) {
      useToastStore
        .getState()
        .showToast(
          "error",
          "Validation Error",
          "Please fill in all missing fields.",
        );
      return Promise.reject(error);
    } else if (status >= 500) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default apiClient;
